import React, { Component, ErrorInfo, ReactNode } from 'react'
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  HomeIcon,
  WifiIcon,
  LockClosedIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { SparklesIcon } from '@heroicons/react/24/solid'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

// 错误边界组件
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }
      return <ErrorFallback onReset={() => this.setState({ hasError: false, error: undefined })} />
    }
    return this.props.children
  }
}

// 错误回退组件 - 豪华版
export const ErrorFallback = ({
  onReset,
  title = '页面出现问题',
  message = '很抱歉，页面遇到了一些问题。请尝试刷新页面或返回首页。'
}: {
  onReset?: () => void
  title?: string
  message?: string
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30 flex items-center justify-center px-4">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-rose-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center overflow-hidden">
          {/* 顶部装饰条 */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-400 via-orange-400 to-amber-400" />

          {/* 图标 */}
          <div className="relative inline-flex mb-6">
            <div className="absolute inset-0 bg-rose-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-rose-50 to-orange-50 rounded-full flex items-center justify-center border border-rose-100">
              <ExclamationTriangleIcon className="h-10 w-10 text-rose-500" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-3">{title}</h2>
          <p className="text-slate-500 mb-8 leading-relaxed">{message}</p>

          <div className="flex flex-col sm:flex-row gap-3">
            {onReset && (
              <button
                onClick={onReset}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3.5 rounded-xl hover:shadow-lg hover:shadow-indigo-200/50 transition-all font-semibold"
              >
                <ArrowPathIcon className="h-5 w-5" />
                重试
              </button>
            )}
            <button
              onClick={() => (window.location.href = '/')}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-6 py-3.5 rounded-xl hover:bg-slate-200 transition-all font-semibold"
            >
              <HomeIcon className="h-5 w-5" />
              返回首页
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// 网络错误组件 - 增强版
export const NetworkError = ({ onRetry }: { onRetry?: () => void }) => {
  return (
    <div className="bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200 rounded-2xl p-8 text-center">
      <div className="relative inline-flex mb-5">
        <div className="absolute inset-0 bg-rose-400/20 rounded-full blur-lg animate-pulse" />
        <div className="relative w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-rose-100">
          <WifiIcon className="h-8 w-8 text-rose-500" />
        </div>
      </div>
      <h3 className="text-lg font-bold text-rose-900 mb-2">网络连接失败</h3>
      <p className="text-rose-700/80 mb-6 text-sm">请检查您的网络连接，然后重试</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-rose-200/50 transition-all font-semibold text-sm"
        >
          <ArrowPathIcon className="h-4 w-4" />
          重新加载
        </button>
      )}
    </div>
  )
}

// 数据为空组件 - 增强版
export const EmptyState = ({
  title = '暂无数据',
  description = '当前没有相关数据',
  icon: Icon = FolderOpenIcon,
  actionText,
  onAction,
  variant = 'default'
}: {
  title?: string
  description?: string
  icon?: React.ElementType
  actionText?: string
  onAction?: () => void
  variant?: 'default' | 'card' | 'minimal'
}) => {
  if (variant === 'minimal') {
    return (
      <div className="text-center py-8">
        <Icon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">{title}</p>
      </div>
    )
  }

  return (
    <div
      className={`text-center ${
        variant === 'card'
          ? 'bg-white rounded-2xl border border-slate-100 shadow-sm'
          : 'bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-2xl border border-dashed border-slate-200'
      } py-16 px-6`}
    >
      <div className="relative inline-flex mb-6">
        <div className="absolute inset-0 bg-indigo-500/10 rounded-full blur-xl" />
        <div className="relative w-20 h-20 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-full flex items-center justify-center border border-slate-200">
          <Icon className="h-10 w-10 text-slate-400" />
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-500 mb-6 max-w-sm mx-auto">{description}</p>
      {actionText && onAction && (
        <button
          onClick={onAction}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-indigo-200/50 transition-all font-semibold"
        >
          <SparklesIcon className="h-4 w-4" />
          {actionText}
        </button>
      )}
    </div>
  )
}

// 权限错误组件 - 增强版
export const PermissionError = ({
  title = '访问受限',
  description = '您没有权限访问此内容，请联系管理员'
}: {
  title?: string
  description?: string
}) => {
  return (
    <div className="text-center py-16">
      <div className="relative inline-flex mb-6">
        <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-xl animate-pulse" />
        <div className="relative w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-full flex items-center justify-center border border-amber-200">
          <LockClosedIcon className="h-10 w-10 text-amber-600" />
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-500 mb-6 max-w-sm mx-auto">{description}</p>
      <button
        onClick={() => window.history.back()}
        className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl hover:bg-slate-200 transition-all font-semibold"
      >
        返回上一页
      </button>
    </div>
  )
}

// 表单错误提示 - 增强版
export const FormError = ({ message }: { message: string }) => {
  return (
    <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-200 rounded-xl animate-in slide-in-from-top-2 duration-200">
      <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
        <XCircleIcon className="h-5 w-5 text-rose-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-rose-800">出错了</p>
        <p className="text-sm text-rose-700 mt-0.5">{message}</p>
      </div>
    </div>
  )
}

// 成功提示 - 增强版
export const SuccessMessage = ({ message, title = '操作成功' }: { message: string; title?: string }) => {
  return (
    <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl animate-in slide-in-from-top-2 duration-200">
      <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
        <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-800">{title}</p>
        <p className="text-sm text-emerald-700 mt-0.5">{message}</p>
      </div>
    </div>
  )
}

// 信息提示 - 新增
export const InfoMessage = ({ message, title = '提示' }: { message: string; title?: string }) => {
  return (
    <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
        <InformationCircleIcon className="h-5 w-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-blue-800">{title}</p>
        <p className="text-sm text-blue-700 mt-0.5">{message}</p>
      </div>
    </div>
  )
}

// 警告提示 - 新增
export const WarningMessage = ({ message, title = '注意' }: { message: string; title?: string }) => {
  return (
    <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
      <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">{title}</p>
        <p className="text-sm text-amber-700 mt-0.5">{message}</p>
      </div>
    </div>
  )
}

export default {
  ErrorBoundary,
  ErrorFallback,
  NetworkError,
  EmptyState,
  PermissionError,
  FormError,
  SuccessMessage,
  InfoMessage,
  WarningMessage
}
